/**
 * PR-AdminUsers-7 — /settings/security
 * Cambiar contraseña propia + gestión de 2FA + sesiones activas.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { ApiError } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useChangePassword, useMySessions, useRevokeSession } from "@/hooks/useMe";
import { cn } from "@/lib/utils";

export function SettingsSecurityPage() {
  return (
    <div className="max-w-2xl space-y-10">
      <div>
        <h1 className="text-2xl font-semibold">Seguridad</h1>
        <p className="text-sm text-muted-foreground">
          Gestiona tu contraseña, segundo factor y sesiones activas.
        </p>
      </div>

      <PasswordSection />
      <TwoFASection />
      <SessionsSection />
    </div>
  );
}

// ── Contraseña ────────────────────────────────────────────────────────────────

function PasswordSection() {
  const [oldPass, setOldPass] = useState("");
  const [newPass, setNewPass] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const changePassword = useChangePassword();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);

    if (newPass !== confirm) {
      setError("Las contraseñas nuevas no coinciden.");
      return;
    }
    if (newPass.length < 8) {
      setError("La contraseña debe tener al menos 8 caracteres.");
      return;
    }

    try {
      await changePassword.mutateAsync({ old_password: oldPass, new_password: newPass });
      setOldPass("");
      setNewPass("");
      setConfirm("");
      setSuccess(true);
    } catch (e) {
      if (e instanceof ApiError) setError(e.message);
      else setError("Error al cambiar la contraseña.");
    }
  };

  return (
    <section className="space-y-4">
      <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
        Contraseña
      </h2>
      <form onSubmit={e => void handleSubmit(e)} className="rounded-lg border bg-card p-5 space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="old-pass">Contraseña actual</Label>
          <Input
            id="old-pass"
            type="password"
            value={oldPass}
            onChange={e => setOldPass(e.target.value)}
            autoComplete="current-password"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="new-pass">Nueva contraseña</Label>
          <Input
            id="new-pass"
            type="password"
            value={newPass}
            onChange={e => setNewPass(e.target.value)}
            autoComplete="new-password"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="confirm-pass">Confirmar nueva contraseña</Label>
          <Input
            id="confirm-pass"
            type="password"
            value={confirm}
            onChange={e => setConfirm(e.target.value)}
            autoComplete="new-password"
          />
        </div>
        {error && (
          <p className="text-sm text-destructive">{error}</p>
        )}
        {success && (
          <p className="text-sm text-emerald-600 dark:text-emerald-400">
            Contraseña cambiada correctamente.
          </p>
        )}
        <Button type="submit" disabled={changePassword.isPending || !oldPass || !newPass || !confirm}>
          {changePassword.isPending ? "Cambiando…" : "Cambiar contraseña"}
        </Button>
      </form>
    </section>
  );
}

// ── 2FA ───────────────────────────────────────────────────────────────────────

function TwoFASection() {
  return (
    <section className="space-y-4">
      <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
        Segundo factor (2FA)
      </h2>
      <div className="rounded-lg border bg-card p-5 space-y-3">
        <p className="text-sm text-muted-foreground">
          Configura o gestiona tu autenticador TOTP desde la pantalla de seguridad de cuenta.
        </p>
        <Link to="/account/security">
          <Button variant="outline" size="sm">
            Gestionar 2FA
          </Button>
        </Link>
      </div>
    </section>
  );
}

// ── Sesiones ──────────────────────────────────────────────────────────────────

function SessionsSection() {
  const sessions = useMySessions();
  const revokeSession = useRevokeSession();
  const [revoking, setRevoking] = useState<string | null>(null);

  const handleRevoke = async (id: string) => {
    setRevoking(id);
    try {
      await revokeSession.mutateAsync(id);
    } finally {
      setRevoking(null);
    }
  };

  return (
    <section className="space-y-4">
      <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
        Sesiones activas
      </h2>
      <div className="rounded-lg border bg-card">
        {sessions.isPending && (
          <p className="px-5 py-4 text-sm text-muted-foreground">Cargando…</p>
        )}
        {sessions.data?.items.length === 0 && (
          <p className="px-5 py-4 text-sm text-muted-foreground">
            No hay sesiones registradas. El tracking de sesiones estará disponible en próxima versión.
          </p>
        )}
        {sessions.data && sessions.data.items.length > 0 && (
          <div className="divide-y">
            {sessions.data.items.map(s => (
              <div key={s.id} className="flex items-center justify-between px-5 py-3 text-sm">
                <div className="space-y-0.5">
                  <div className="font-medium">
                    {s.ip_address ?? "IP desconocida"}
                    {" "}
                    <span className={cn(
                      "rounded-full px-1.5 py-0.5 text-[10px] font-semibold",
                      s.is_active
                        ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400"
                        : "bg-muted text-muted-foreground",
                    )}>
                      {s.is_active ? "Activa" : "Revocada"}
                    </span>
                  </div>
                  <div className="text-xs text-muted-foreground truncate max-w-sm">
                    {s.user_agent ?? "Agente desconocido"}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Iniciada: {new Date(s.created_at).toLocaleString("es-MX")}
                    {" · "}
                    Expira: {new Date(s.expires_at).toLocaleString("es-MX")}
                  </div>
                </div>
                {s.is_active && (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-destructive hover:text-destructive shrink-0"
                    onClick={() => void handleRevoke(s.id)}
                    disabled={revoking === s.id}
                  >
                    Revocar
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
